/**
 * Contact Type Selector Dialog
 *
 * Dialog component for selecting between Individual and Organization contact types
 * before creating a new contact.
 */

import { Building2, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { ContactType } from '../types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ContactTypeSelectorDialog({ open, onOpenChange }: Props) {
  const navigate = useNavigate();

  const handleSelectType = (contactType: ContactType) => {
    // Navigate to create page with contact_type query parameter
    navigate(`/contacts/create?type=${contactType}`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Select Contact Type</DialogTitle>
          <DialogDescription>Choose the type of contact you want to create</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 py-4">
          {/* Individual Option */}
          <Button
            variant="outline"
            className="h-32 flex flex-col items-center justify-center gap-3 hover:bg-accent hover:border-primary"
            onClick={() => handleSelectType('individual')}
          >
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="h-6 w-6 text-primary" />
            </div>
            <div className="text-center">
              <div className="font-semibold text-sm">Individual</div>
              <div className="text-xs text-muted-foreground mt-1">Person contact</div>
            </div>
          </Button>

          {/* Organization Option */}
          <Button
            variant="outline"
            className="h-32 flex flex-col items-center justify-center gap-3 hover:bg-accent hover:border-primary"
            onClick={() => handleSelectType('organization')}
          >
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <div className="text-center">
              <div className="font-semibold text-sm">Organization</div>
              <div className="text-xs text-muted-foreground mt-1">Company or entity</div>
            </div>
          </Button>
        </div>

        <div className="text-xs text-muted-foreground text-center">
          Select an option above to continue
        </div>
      </DialogContent>
    </Dialog>
  );
}

ContactTypeSelectorDialog.displayName = 'ContactTypeSelectorDialog';
